import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CHURCH } from "@/lib/church";
import { Heart, Flower, PaperPlaneTilt, Sparkle, ShieldCheck } from "@phosphor-icons/react";

const INITIAL = {
    tribute_type: "memory",
    honoree_name: "",
    giver_name: "",
    giver_email: "",
    giver_phone: "",
    recipient_name: "",
    recipient_email: "",
    message: "",
    amount: "",
    notify_recipient: true,
};

export default function TributeGifts() {
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/tribute-gift", form);
            setSent(true);
            toast.success("Tribute recorded. Notifications on the way.");
        } catch (_err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSending(false);
        }
    };

    const isMemory = form.tribute_type === "memory";

    return (
        <section id="tribute-gifts" className="relative bg-[#F8F5F0] py-24 lg:py-32 overflow-hidden scroll-mt-24" data-testid="tribute-section">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#FFC72C]/15 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#4C1D95]/10 blur-3xl rounded-full" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    {/* LEFT — Editorial intro */}
                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-32">
                            <div className="inline-flex items-center gap-3 mb-6">
                                <span className="w-10 h-px bg-[#4C1D95]" />
                                <p className="overline text-[#4C1D95]">Tribute &amp; Memorial Gifts</p>
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-[#2E1A47] mb-8" data-testid="tribute-heading">
                                A gift, given in <span className="italic text-[#4C1D95]">someone&rsquo;s name</span>.
                            </h2>
                            <p className="text-base lg:text-lg leading-relaxed text-slate-700 mb-6">
                                Some offerings carry more than dollars — they carry a name, a memory, a life we&rsquo;re grateful for. Make a gift <em className="text-[#4C1D95] font-medium">in loving memory</em> of a loved one, or <em className="text-[#4C1D95] font-medium">in honor</em> of someone still walking with us.
                            </p>
                            <p className="text-base lg:text-lg leading-relaxed text-slate-700 mb-10">
                                We will notify the family or honoree with a dignified letter announcing that a tribute was made in their name — the amount is never shared.
                            </p>
                            <div className="pt-6 border-t border-[#2E1A47]/10 space-y-3">
                                <div className="flex items-start gap-3">
                                    <ShieldCheck size={20} weight="duotone" className="text-[#4C1D95] shrink-0 mt-0.5" />
                                    <p className="text-sm text-slate-600 leading-relaxed"><span className="font-bold text-[#2E1A47]">Private and dignified.</span> Gift amounts are never disclosed to the recipient.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Sparkle size={20} weight="duotone" className="text-[#4C1D95] shrink-0 mt-0.5" />
                                    <p className="text-sm text-slate-600 leading-relaxed"><span className="font-bold text-[#2E1A47]">A living record.</span> Each tribute is entered into the church&rsquo;s remembrance book.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — Form or Sent card */}
                    <div className="lg:col-span-7">
                        <div className="bg-white shadow-[0_35px_80px_-25px_rgba(76,29,149,0.35)] border-t-4 border-[#FFC72C]">
                            {sent ? (
                                <div className="p-10 lg:p-14 text-center" data-testid="tribute-sent">
                                    <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#FFC72C] to-[#E6B327] flex items-center justify-center mb-6 shadow-lg">
                                        <Flower size={36} weight="duotone" className="text-[#2E1A47]" />
                                    </div>
                                    <p className="overline text-[#4C1D95] mb-3">Tribute Received</p>
                                    <h3 className="font-display text-3xl lg:text-4xl font-black tracking-tighter text-[#2E1A47] leading-[0.95] mb-4">
                                        A name has been <span className="italic text-[#4C1D95]">honored.</span>
                                    </h3>
                                    <p className="text-slate-600 leading-relaxed mb-2">
                                        Your tribute for <span className="font-bold text-[#2E1A47]">{form.honoree_name}</span> has been recorded.
                                    </p>
                                    {form.notify_recipient && (
                                        <p className="text-sm text-slate-500 leading-relaxed">
                                            A dignified letter is on its way to <span className="font-bold text-[#2E1A47]">{form.recipient_name}</span>.
                                        </p>
                                    )}
                                    <div className="w-16 h-px bg-[#FFC72C] mx-auto my-8" />
                                    <p className="text-sm text-slate-600 mb-6">Ready to make the offering?</p>
                                    <div className="flex flex-wrap justify-center gap-3">
                                        <a
                                            href={CHURCH.giveUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="tribute-give-cta"
                                            className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-[#2E1A47] px-8 py-4 text-[11px] font-black uppercase tracking-[0.25em] transition-colors"
                                        >
                                            <Heart size={14} weight="fill" /> Give Online
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => { setSent(false); setForm(INITIAL); }}
                                            data-testid="tribute-another-btn"
                                            className="inline-flex items-center gap-2 border-2 border-[#4C1D95] hover:bg-[#4C1D95] hover:text-white text-[#4C1D95] px-8 py-4 text-[11px] font-black uppercase tracking-[0.25em] transition-colors"
                                        >
                                            Record Another
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="p-8 lg:p-12 space-y-6" data-testid="tribute-form">
                                    {/* Type toggle */}
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.25em] font-black text-[#4C1D95] mb-3">This gift is</p>
                                        <div className="grid grid-cols-2 gap-2 p-1 bg-[#F8F5F0]">
                                            {[{ v: "memory", label: "In Loving Memory of" }, { v: "honor", label: "In Honor of" }].map((opt) => {
                                                const active = form.tribute_type === opt.v;
                                                return (
                                                    <button
                                                        key={opt.v}
                                                        type="button"
                                                        onClick={() => update("tribute_type", opt.v)}
                                                        data-testid={`tribute-type-${opt.v}`}
                                                        className={`px-4 py-3.5 text-xs font-black uppercase tracking-[0.15em] transition-colors ${active ? 'bg-[#2E1A47] text-[#FFC72C] shadow-md' : 'text-[#4C1D95] hover:bg-white'}`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Honoree name — hero field */}
                                    <div>
                                        <label htmlFor="honoree" className="block text-[10px] uppercase tracking-[0.25em] font-black text-[#4C1D95] mb-2">
                                            {isMemory ? "The name we honor" : "The name we celebrate"}
                                        </label>
                                        <input
                                            id="honoree"
                                            data-testid="tribute-honoree"
                                            required
                                            value={form.honoree_name}
                                            onChange={(e) => update("honoree_name", e.target.value)}
                                            placeholder="Full name"
                                            className="w-full px-0 py-3 text-2xl lg:text-3xl font-display font-bold tracking-tight text-[#2E1A47] placeholder-slate-300 bg-transparent border-b-2 border-[#4C1D95]/20 focus:border-[#FFC72C] focus:outline-none transition-colors"
                                        />
                                    </div>

                                    {/* Giver block */}
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.25em] font-black text-[#4C1D95] mb-3">From the giver</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <input
                                                required
                                                data-testid="tribute-giver-name"
                                                value={form.giver_name}
                                                onChange={(e) => update("giver_name", e.target.value)}
                                                placeholder="Your name"
                                                className="w-full px-4 py-3 rounded-md border border-slate-200 focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/15 focus:outline-none text-sm text-[#2E1A47] placeholder-slate-400"
                                            />
                                            <input
                                                required
                                                type="email"
                                                data-testid="tribute-giver-email"
                                                value={form.giver_email}
                                                onChange={(e) => update("giver_email", e.target.value)}
                                                placeholder="Your email"
                                                className="w-full px-4 py-3 rounded-md border border-slate-200 focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/15 focus:outline-none text-sm text-[#2E1A47] placeholder-slate-400"
                                            />
                                            <input
                                                data-testid="tribute-giver-phone"
                                                value={form.giver_phone}
                                                onChange={(e) => update("giver_phone", e.target.value)}
                                                placeholder="Phone (optional)"
                                                className="w-full px-4 py-3 rounded-md border border-slate-200 focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/15 focus:outline-none text-sm text-[#2E1A47] placeholder-slate-400"
                                            />
                                            <input
                                                data-testid="tribute-amount"
                                                value={form.amount}
                                                onChange={(e) => update("amount", e.target.value)}
                                                placeholder="Amount (optional)"
                                                className="w-full px-4 py-3 rounded-md border border-slate-200 focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/15 focus:outline-none text-sm text-[#2E1A47] placeholder-slate-400"
                                            />
                                        </div>
                                    </div>

                                    {/* Recipient block */}
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.25em] font-black text-[#4C1D95] mb-3">Notify the family or honoree</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <input
                                                required
                                                data-testid="tribute-recipient-name"
                                                value={form.recipient_name}
                                                onChange={(e) => update("recipient_name", e.target.value)}
                                                placeholder="Their name"
                                                className="w-full px-4 py-3 rounded-md border border-slate-200 focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/15 focus:outline-none text-sm text-[#2E1A47] placeholder-slate-400"
                                            />
                                            <input
                                                required
                                                type="email"
                                                data-testid="tribute-recipient-email"
                                                value={form.recipient_email}
                                                onChange={(e) => update("recipient_email", e.target.value)}
                                                placeholder="Their email"
                                                className="w-full px-4 py-3 rounded-md border border-slate-200 focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/15 focus:outline-none text-sm text-[#2E1A47] placeholder-slate-400"
                                            />
                                        </div>
                                        <label className="flex items-start gap-3 mt-3 cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                data-testid="tribute-notify"
                                                checked={form.notify_recipient}
                                                onChange={(e) => update("notify_recipient", e.target.checked)}
                                                className="mt-1 w-4 h-4 accent-[#4C1D95] cursor-pointer"
                                            />
                                            <span className="text-xs text-slate-600 leading-relaxed">
                                                <span className="font-bold text-[#2E1A47]">Send a dignified letter</span> letting them know a gift was made in {isMemory ? "memory" : "honor"} of <span className="font-bold text-[#2E1A47]">{form.honoree_name || "…"}</span>. The amount will not be shared.
                                            </span>
                                        </label>
                                    </div>

                                    {/* Optional message */}
                                    <div>
                                        <label htmlFor="tribute-note" className="text-[10px] uppercase tracking-[0.25em] font-black text-[#4C1D95] mb-2 block">
                                            A word from you (optional)
                                        </label>
                                        <textarea
                                            id="tribute-note"
                                            data-testid="tribute-message"
                                            rows={3}
                                            value={form.message}
                                            onChange={(e) => update("message", e.target.value)}
                                            placeholder="Share a memory, a scripture, a word of blessing…"
                                            className="w-full px-4 py-3 rounded-md border border-slate-200 focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/15 focus:outline-none text-sm text-[#2E1A47] placeholder-slate-400 resize-none"
                                        />
                                    </div>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        data-testid="tribute-submit"
                                        className="w-full inline-flex items-center justify-center gap-3 bg-[#2E1A47] hover:bg-[#4C1D95] disabled:opacity-70 text-[#FFC72C] px-8 py-5 text-[11px] font-black uppercase tracking-[0.3em] transition-colors shadow-lg"
                                    >
                                        {sending ? "Recording…" : (<><PaperPlaneTilt size={14} weight="fill" /> Record This Tribute</>)}
                                    </button>
                                    <p className="text-[11px] text-center text-slate-500 leading-relaxed">
                                        After submitting, you&rsquo;ll be invited to complete your gift online. Your tribute will be entered into the remembrance book regardless of whether the online gift is completed.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
